/**
 * #4619 Slice 1 (amended per Codex exact-head review) — ExecutiveAnswerContract
 * (shared contracts only, zero runtime change).
 *
 * The declarative, versioned, module-owned, wording-independent contract that says what
 * an ANSWER to a governed intent must contain: its obligations, the semantic roles the
 * bound numbers must play, the analytical method behind computed shapes, the evidence it
 * rests on, and whether a recommendation is not applicable, optional, or required.
 *
 * Lineage rule (binding — issue #4619 comment 5306285802): this EXTENDS the
 * `answerAnchors` family on `GovernedCapabilityIntent`, it does not replace it and it is
 * not a parallel contract system. Foundry will generate/validate these same artifacts
 * later; one canonical intent definition must drive planner catalog, runtime binding,
 * answer compilation, render contracts, oracle expectations, and Judge fixtures.
 *
 * Declaring a contract changes NO runtime behaviour today. It is inert data until the
 * answer-context compiler and outbound validator (later slices) consume it.
 */

/**
 * The SHAPE an answer takes — the epic's canonical six, exactly. Slice 1's richer
 * nine-value family is RETIRED: what it expressed as extra shape values is now
 * structured metadata under these six —
 *
 * - ex-`bridge`                  → `decomposition` + `methodDescriptor: 'value_bridge'`
 * - ex-`projection`              → `scenario` + `methodDescriptor: 'projection'`
 * - ex-`comparison_to_benchmark` → `comparison` + `comparisonBasis:
 *   'against_benchmark'` (or `'against_target'` for target-gap comparisons)
 * - ex-`comparison_of_options`   → `comparison` + `comparisonBasis: 'between_options'`
 * - ex-`ranking`                 → `comparison` + `methodDescriptor: 'ranking'`
 * - ex-`scenario_model`          → `scenario` + `methodDescriptor: 'scenario_model'`
 * - ex-`decision_package`        → `decision` + `methodDescriptor: 'decision_package'`
 *
 * `GOVERNED_CAPABILITY_ANSWER_SHAPES` (answerAnchors) remains unchanged — migrating
 * anchors onto this family is a later slice.
 */
export const EXECUTIVE_ANSWER_SHAPES = frozenTuple([
  'lookup',
  'comparison',
  'decomposition',
  'diagnostic',
  'scenario',
  'decision',
] as const);

export type ExecutiveAnswerShape = (typeof EXECUTIVE_ANSWER_SHAPES)[number];

/**
 * Shapes whose answers are COMPUTED, not read: they must name the governed analytical
 * method that produces them (`analyticalMethodRef`). Only a pure `lookup` readout binds
 * a figure without computing one.
 */
export const EXECUTIVE_ANSWER_SHAPES_IMPLYING_COMPUTATION = frozenTuple([
  'comparison',
  'decomposition',
  'diagnostic',
  'scenario',
  'decision',
] as const);

export function executiveAnswerShapeImpliesComputation(shape: ExecutiveAnswerShape): boolean {
  return isOneOf(shape, EXECUTIVE_ANSWER_SHAPES_IMPLYING_COMPUTATION);
}

/**
 * CLOSED method descriptors — the structured metadata that re-expresses Slice 1's
 * retired shape values under the canonical six. Closed enum, never free text: a
 * descriptor is a fence input, and free text cannot fence.
 */
export const EXECUTIVE_ANSWER_METHOD_DESCRIPTORS = frozenTuple([
  'value_bridge',
  'projection',
  'ranking',
  'scenario_model',
  'decision_package',
] as const);

export type ExecutiveAnswerMethodDescriptor = (typeof EXECUTIVE_ANSWER_METHOD_DESCRIPTORS)[number];

/**
 * Which canonical shapes each method descriptor may pair with — exported as data so
 * authoring tools, Foundry generation, and the validator read one source of truth.
 */
export const EXECUTIVE_ANSWER_METHOD_DESCRIPTOR_SHAPES: Readonly<
  Record<ExecutiveAnswerMethodDescriptor, readonly ExecutiveAnswerShape[]>
> = Object.freeze({
  value_bridge: frozenTuple(['decomposition', 'diagnostic'] as const),
  projection: frozenTuple(['scenario'] as const),
  ranking: frozenTuple(['comparison'] as const),
  scenario_model: frozenTuple(['scenario'] as const),
  decision_package: frozenTuple(['decision'] as const),
});

/**
 * What a `comparison` answer compares AGAINST — the structured split that Slice 1
 * encoded as two shape values (`comparison_to_benchmark` / `comparison_of_options`).
 * Required exactly when `answerShape` is `comparison`; invalid on every other shape.
 */
export const EXECUTIVE_ANSWER_COMPARISON_BASES = frozenTuple([
  'against_benchmark',
  'against_target',
  'between_options',
] as const);

export type ExecutiveAnswerComparisonBasis = (typeof EXECUTIVE_ANSWER_COMPARISON_BASES)[number];

/**
 * The ROLE an obligation plays in the answer — the epic's canonical five. This is the
 * obligation-level vocabulary (what the answer must DO); the semantic number roles
 * below are the binding-level vocabulary (what a bound number IS).
 */
export const EXECUTIVE_ANSWER_OBLIGATION_ROLES = frozenTuple([
  'direct_answer',
  'comparison',
  'driver',
  'gap',
  'decision',
] as const);

export type ExecutiveAnswerObligationRole = (typeof EXECUTIVE_ANSWER_OBLIGATION_ROLES)[number];

/**
 * The semantic ROLE a bound number plays in an answer. The union is CLOSED.
 *
 * - `actual`                  — the governed current value
 * - `baseline`                — the reference value a change/target is measured from
 * - `target`                  — the committed/approved value
 * - `benchmark`               — an external/peer reference the actual is compared against
 * - `variance`                — a computed difference/trend
 * - `component`               — one part of a decomposition
 * - `driver`                  — a factor explaining movement in the subject metric
 * - `current_loss`            — the governed loss run-rate being addressed
 * - `exposure`                — unrealized/at-risk value
 * - `addressable_opportunity` — a sized recoverable value (Slice 1's `opportunity`)
 * - `gross_ceiling`           — the gross recoverable before cost/feasibility haircuts
 * - `expected_outcome`        — the net outcome a decision is expected to deliver
 * - `scenario_value`          — a modeled what-if outcome
 * - `forecast`                — a projected future value on the current trajectory
 * - `realized_value`          — a measured post-decision outcome
 * - `concentration_measure`   — a governed measure of portfolio concentration
 *
 * Slice 1's `denominator` is DROPPED: no migrated manifest obligation requires the
 * ratio base as a first-class answer role, and additive roles need producer evidence.
 * This union is closed: additions require an authored contract-vocabulary amendment
 * and the exact-union conformance pin; callers may never mint roles ad hoc.
 */
export const SEMANTIC_NUMBER_ROLES = frozenTuple([
  'actual',
  'baseline',
  'target',
  'benchmark',
  'variance',
  'component',
  'driver',
  'current_loss',
  'exposure',
  'addressable_opportunity',
  'gross_ceiling',
  'expected_outcome',
  'scenario_value',
  'forecast',
  'realized_value',
  'concentration_measure',
] as const);

export type SemanticNumberRole = (typeof SEMANTIC_NUMBER_ROLES)[number];

export type ProhibitedRoleSubstitution = {
  /** The role a produced number actually plays. */
  readonly offeredRole: SemanticNumberRole;
  /** The role it must never be presented as. */
  readonly claimedRole: SemanticNumberRole;
  /** The defect class the substitution would commit — the validator's error vocabulary. */
  readonly defectClass: string;
};

/**
 * The CLOSED table of forbidden role substitutions, exported as data so the outbound
 * validator (later slice) and Judge fixtures read one source of truth.
 *
 * The epic's two mandated structural fences lead the table:
 * - opportunity-as-current-loss: a sized recovery never satisfies a current-loss
 *   obligation (the money you could get back is not the money you are losing);
 * - ceiling-as-expected-outcome: a gross ceiling never satisfies expected_outcome
 *   (the theoretical maximum is not what the decision is expected to deliver).
 *
 * Then the Slice 1 pairs, re-expressed in canonical roles:
 * - benchmark-as-actual: a peer/industry reference presented as the company's own value;
 * - component-as-actual: a subset presented as the whole (subset-as-whole);
 * - scenario-as-realized: a modeled what-if presented as a measured outcome;
 * - opportunity-as-actual: a sized recovery presented as current performance.
 *
 * Boundary note: modeled-as-measured is a sourceClass concern, NOT a role pair. The
 * roles table governs the semantic identity of a number inside one answer; whether the
 * number was measured, approved, or modeled is provenance, owned by the sourceClass /
 * provenance-badge machinery (Standing Law 15). `scenario_value` vs `realized_value`
 * stays in THIS table because both are legitimate governed roles and the defect is
 * presenting one role as the other, not a provenance downgrade.
 */
export const PROHIBITED_ROLE_SUBSTITUTIONS: readonly ProhibitedRoleSubstitution[] =
  deepFrozenSubstitutions([
    {
      offeredRole: 'addressable_opportunity',
      claimedRole: 'current_loss',
      defectClass: 'opportunity_presented_as_current_loss',
    },
    {
      offeredRole: 'gross_ceiling',
      claimedRole: 'expected_outcome',
      defectClass: 'gross_ceiling_presented_as_expected_outcome',
    },
    {
      offeredRole: 'benchmark',
      claimedRole: 'actual',
      defectClass: 'benchmark_presented_as_actual',
    },
    {
      offeredRole: 'component',
      claimedRole: 'actual',
      defectClass: 'subset_presented_as_whole',
    },
    {
      offeredRole: 'scenario_value',
      claimedRole: 'realized_value',
      defectClass: 'scenario_presented_as_realized',
    },
    {
      offeredRole: 'addressable_opportunity',
      claimedRole: 'actual',
      defectClass: 'opportunity_presented_as_actual',
    },
    {
      offeredRole: 'target',
      claimedRole: 'actual',
      defectClass: 'target_presented_as_actual',
    },
    {
      offeredRole: 'forecast',
      claimedRole: 'actual',
      defectClass: 'forecast_presented_as_actual',
    },
    {
      offeredRole: 'gross_ceiling',
      claimedRole: 'current_loss',
      defectClass: 'gross_ceiling_presented_as_current_loss',
    },
  ]);

export function isProhibitedRoleSubstitution(
  offeredRole: SemanticNumberRole,
  claimedRole: SemanticNumberRole
): boolean {
  return PROHIBITED_ROLE_SUBSTITUTIONS.some(
    (pair) => pair.offeredRole === offeredRole && pair.claimedRole === claimedRole
  );
}

/**
 * The CLOSED relationship vocabulary for admissible facts, derived exactly from the
 * epic's admission formula — an answer may carry: required subject metrics
 * (`subject_metric`), direct calculation dependencies, benchmark/target dependencies,
 * specifically permitted explanatory facts (`explanatory_driver`), and decision facts
 * under the recommendation policy (`decision_support`). Nothing else is admissible.
 */
export const EXECUTIVE_ANSWER_FACT_RELATIONSHIP_TYPES = frozenTuple([
  'subject_metric',
  'direct_calculation_dependency',
  'benchmark_dependency',
  'target_dependency',
  'explanatory_driver',
  'decision_support',
] as const);

export type ExecutiveAnswerFactRelationshipType =
  (typeof EXECUTIVE_ANSWER_FACT_RELATIONSHIP_TYPES)[number];

/** The CLOSED grain vocabulary for scope constraints on admissible facts. */
export const EXECUTIVE_ANSWER_SCOPE_GRAINS = frozenTuple([
  'enterprise',
  'objective',
  'module',
  'lever',
  'metric',
] as const);

export type ExecutiveAnswerScopeGrain = (typeof EXECUTIVE_ANSWER_SCOPE_GRAINS)[number];

/**
 * An entity/grain constraint on an admissible fact. At least one of the two fields
 * must be present — an empty constraint constrains nothing and is rejected.
 */
export type ExecutiveAnswerScopeConstraint = {
  /** Governed entity/scope id the fact must belong to (module id, objective id, ...). */
  readonly entityRef?: string;
  readonly grain?: ExecutiveAnswerScopeGrain;
};

/**
 * The declared admissible-binding constraint an obligation may carry INSTEAD of (or in
 * addition to) `metricKeys`: the semantic role the binding must play PLUS the declared
 * relationship to the answer subject, optionally scope-constrained. This is what makes
 * key-less obligations still machine-enforceable — never a bare role that any binding
 * with a matching semantic role could satisfy.
 */
export type ExecutiveAnswerAdmissibleBinding = {
  readonly semanticRole: SemanticNumberRole;
  readonly relationshipType: ExecutiveAnswerFactRelationshipType;
  readonly scopeConstraint?: ExecutiveAnswerScopeConstraint;
};

/**
 * The honest-gap fallback (the epic's `permittedGap`): when a required obligation
 * cannot be met this turn, the answer must NAME the gap with this statement instead of
 * silently substituting a differently-roled number. An honest named gap is a passing
 * state.
 */
export type ExecutiveAnswerPermittedGap = {
  readonly statement: string;
  /** Who could close the gap (evidence owner, connector, approver) — prose hint. */
  readonly ownerHint?: string;
};

/**
 * One obligation: what the answer must do (`role`), whether it may be skipped
 * (`required`), and — mandatorily — HOW satisfaction is judged: a governed
 * `metricKeys` list (aligning with the `requiredEvidenceKeys` /
 * `numericBindingsProduced` key conventions), or an explicit `admissibleBinding`
 * relationship/grain constraint. Role-only satisfaction is rejected by the validator.
 */
export type ExecutiveAnswerObligation = {
  readonly obligationId: string;
  readonly description: string;
  readonly role: ExecutiveAnswerObligationRole;
  readonly required: boolean;
  /** Non-empty when present. When both this and `admissibleBinding` are present, the
   * keys are the tighter constraint and win. */
  readonly metricKeys?: readonly string[];
  /**
   * Optional exact public-binding identities that further narrow `metricKeys`.
   *
   * This is an AND constraint, never an alias for a computation address: the
   * binding must still resolve one of `metricKeys` through its governed
   * `valuePath`, and its placeholder must exactly equal one of these refs. It
   * lets a scenario contract select its scenario-scoped presentation binding
   * without allowing a same-named placeholder to override a different
   * computation address.
   */
  readonly bindingPlaceholderRefs?: readonly string[];
  readonly admissibleBinding?: ExecutiveAnswerAdmissibleBinding;
  readonly permittedGap?: ExecutiveAnswerPermittedGap;
};

/**
 * One structured supporting-fact admission: facts playing `role`, related to the
 * answer subject via `relationshipType`, optionally pinned to metric keys and/or an
 * entity/grain constraint, are PERMITTED beyond the obligations. This replaces
 * Slice 1's prose `relevanceScope` — the answer-wide relevance fence the context
 * compiler (later slice) will enforce, machine-readable end to end.
 */
export type ExecutiveAnswerPermittedSupportingRole = {
  readonly role: SemanticNumberRole;
  readonly relationshipType: ExecutiveAnswerFactRelationshipType;
  /** Non-empty when present. */
  readonly metricKeyRefs?: readonly string[];
  /** Exact binding-identity filter applied in addition to `metricKeyRefs`. */
  readonly bindingPlaceholderRefs?: readonly string[];
  readonly scopeConstraint?: ExecutiveAnswerScopeConstraint;
};

export const EXECUTIVE_ANSWER_RECOMMENDATION_POLICIES = frozenTuple([
  'not_applicable',
  'optional',
  'required',
] as const);

export type ExecutiveAnswerRecommendationPolicy =
  (typeof EXECUTIVE_ANSWER_RECOMMENDATION_POLICIES)[number];

/**
 * #6139 (PR #6143 HOLD, cure 1) — the NARRATION-INSTRUCTION MIGRATION MARKER.
 *
 * Closed vocabulary, one member. A contract carrying
 * `narrationInstructions: 'contract_v1'` declares that the turn's narration
 * instruction set may be projected from THIS contract's typed fields
 * (`answerShape`, `recommendationPolicy`, `sectionPolicy`, obligations). A
 * contract WITHOUT the marker — including the contract-bearing intents that
 * predate the projection seam — is treated exactly like an intent with no
 * authored contract at all: the narrator keeps the legacy full instruction
 * block. This is lifecycle staging declared on the contract itself, never an
 * intent-id check in the narrator.
 */
export const EXECUTIVE_ANSWER_NARRATION_INSTRUCTION_MODES = frozenTuple([
  'contract_v1',
] as const);

export type ExecutiveAnswerNarrationInstructionMode =
  (typeof EXECUTIVE_ANSWER_NARRATION_INSTRUCTION_MODES)[number];

/**
 * Where the authority for an applicable recommendation comes from: a producer-run
 * governed scenario, or a planner-assembled decision package. Never the narrator.
 */
export const EXECUTIVE_ANSWER_RECOMMENDATION_AUTHORITY_SOURCES = frozenTuple([
  'producer_scenario',
  'planner_package',
] as const);

export type ExecutiveAnswerRecommendationAuthoritySource =
  (typeof EXECUTIVE_ANSWER_RECOMMENDATION_AUTHORITY_SOURCES)[number];

export const EXECUTIVE_ANSWER_SECTION_DISPOSITIONS = frozenTuple([
  'required',
  'optional',
  'disabled',
] as const);

export type ExecutiveAnswerSectionDisposition =
  (typeof EXECUTIVE_ANSWER_SECTION_DISPOSITIONS)[number];

/**
 * The STRUCTURED section policy — not an arbitrary record. The direct answer is the
 * one section no contract may omit or disable (`directAnswer` is literally typed
 * `'required'`); the recommendation section's disposition must agree with
 * `recommendationPolicy` (`not_applicable` ⇒ `disabled`, `optional` ⇒ `optional`,
 * `required` ⇒ `required` — anything else is a contradiction the validator rejects).
 * Additional sections (whatChanged, rationale, ...) ride `supportingSections`.
 *
 * #4741 — `headline` is NOT one of them. Governed narration renders the direct
 * answer AS its `headline` section, so a `supportingSections.headline` entry would
 * be a SECOND truth about the one property `directAnswer` already settles: a
 * contract could swear `directAnswer: 'required'` and, in the same policy, switch
 * off the section that carries it. `directAnswer` is the single authority and the
 * validator reserves the key, exactly as it reserves `recommendation`.
 */
export type ExecutiveAnswerSectionPolicy = {
  readonly directAnswer: 'required';
  readonly recommendation: ExecutiveAnswerSectionDisposition;
  readonly supportingSections?: Readonly<Record<string, ExecutiveAnswerSectionDisposition>>;
};

/** The section disposition each recommendation policy mandates. */
const SECTION_DISPOSITION_BY_RECOMMENDATION_POLICY: Readonly<
  Record<ExecutiveAnswerRecommendationPolicy, ExecutiveAnswerSectionDisposition>
> = Object.freeze({
  not_applicable: 'disabled',
  optional: 'optional',
  required: 'required',
});

/**
 * WHAT THIS CONTRACT OWES ITS READER IN THE RECOMMENDATION SECTION.
 *
 * `required` — a recommendation is owed; its absence is a NAMED GAP.
 * `optional` — a recommendation is permitted; its absence is just an absence,
 *              and copy that calls it missing states a requirement the contract
 *              does not declare.
 * `disabled` — the shape does not advise at all (a pure lookup readout).
 * `null`     — no authored contract, or none declared: the honest state of an
 *              unmigrated intent, never a guess about it. Callers keep whatever
 *              behaviour they had before contracts existed.
 *
 * ── #4779 AMENDMENT 18, BLOCKER 2 ───────────────────────────────────────────
 *
 * This exists so there is ONE typed read of the recommendation policy rather
 * than one per consumer. Composition had two: the gap surface asked whether the
 * policy was `required` before naming a missing recommendation, while the
 * recommendation/rationale SLOTS asked only whether an authority was declared
 * and produced nothing — policy unread. A contract declaring `optional` with a
 * producer authority therefore replaced its ordinary prose, on every ordinary
 * turn, with copy asserting "This answer requires a recommendation … and none
 * was produced" — a requirement its own contract does not state, beside a gap
 * surface that (correctly) named nothing. The disposition is read here, once,
 * and consumed.
 */
export function recommendationSectionDisposition(
  contract: ExecutiveAnswerContract | null | undefined
): ExecutiveAnswerSectionDisposition | null {
  const policy = contract?.recommendationPolicy;
  if (typeof policy !== 'string') return null;
  return SECTION_DISPOSITION_BY_RECOMMENDATION_POLICY[policy] ?? null;
}

/**
 * The default recommendation policy for a shape, exported as data so authoring tools
 * and Foundry generation share one rule: a pure lookup readout defaults to
 * 'not_applicable' (a readout that starts advising is the Board-answer defect), a
 * decision exists to recommend, everything else defaults to 'optional'.
 */
export function defaultRecommendationPolicyForShape(
  shape: ExecutiveAnswerShape
): ExecutiveAnswerRecommendationPolicy {
  if (shape === 'lookup') return 'not_applicable';
  if (shape === 'decision') return 'required';
  return 'optional';
}

/**
 * Does this intent's authored contract declare a DECISION-shaped answer?
 *
 * The one question a composition seam may ask about an answer's shape. It is
 * a DECLARATION read — the intent said what its answer is — never an
 * inference from module leftovers (an absent narrative frame, an empty
 * scenario-options array) and never a read of the reader's wording. An intent
 * with no authored contract answers `false`, which is the honest state of an
 * unmigrated intent, not a guess about it.
 *
 * Lives here beside the shape family so composition and validation ask the
 * same question of the same field, and neither re-expresses it locally.
 */
export function declaresDecisionShapedAnswer(
  contract: ExecutiveAnswerContract | null | undefined
): boolean {
  return contract?.answerShape === 'decision';
}

/**
 * #6139 (PR #6143 HOLD, cure 1) — the ONE read of the narration-instruction
 * migration marker, beside the shape read so every consumer asks the same
 * question of the same field. A contract opts its turn's narration
 * instructions into contract projection only by carrying the marker;
 * everything else — no contract, or a contract without it — keeps the legacy
 * instruction block. Absent marker is never treated as a malformed value:
 * the validator rejects unknown members, so a present-and-valid marker is
 * the only `true` state.
 */
export function declaresContractProjectedNarrationInstructions(
  contract: ExecutiveAnswerContract | null | undefined
): boolean {
  return (
    contract?.narrationInstructions !== undefined &&
    isOneOf(
      contract.narrationInstructions,
      EXECUTIVE_ANSWER_NARRATION_INSTRUCTION_MODES
    )
  );
}

export type ExecutiveAnswerContract = {
  /** Semver-ish contract version (e.g. "1.0.0"). Foundry bumps it on regeneration. */
  readonly contractVersion: string;
  readonly answerShape: ExecutiveAnswerShape;
  /** Required exactly when `answerShape` is `comparison`; invalid otherwise. */
  readonly comparisonBasis?: ExecutiveAnswerComparisonBasis;
  /** Optional closed-enum method family; must pair with a permitted shape. */
  readonly methodDescriptor?: ExecutiveAnswerMethodDescriptor;
  /** What the answer must contain. Never empty — an obligation-free contract is hollow. */
  readonly obligations: readonly ExecutiveAnswerObligation[];
  /**
   * Governed analytical method reference (formula ref / method id). Required non-empty
   * for every shape that implies computation (everything except `lookup`).
   */
  readonly analyticalMethodRef?: string;
  /** Evidence refs, aligning with the manifest `requiredEvidenceKeys` conventions. */
  readonly evidenceRequirements: readonly string[];
  /** Expected Render Contract reference — Foundry render lineage, optional in Slice 1. */
  readonly renderContractRef?: string;
  readonly recommendationPolicy: ExecutiveAnswerRecommendationPolicy;
  /** Only meaningful when recommendationPolicy is not 'not_applicable'. */
  readonly recommendationAuthoritySource?: ExecutiveAnswerRecommendationAuthoritySource;
  /** Mandatory structured section policy — see ExecutiveAnswerSectionPolicy. */
  readonly sectionPolicy: ExecutiveAnswerSectionPolicy;
  /**
   * #5019 — the generic required executive close. When 'required', the narrated
   * recommendation must mechanically carry the four close elements
   * (recommendation, alternative/tradeoff, owner+timing, decision ask),
   * validated deterministically and repairable. Absent ⇒ 'optional'.
   */
  readonly executiveClosePolicy?: 'required' | 'optional';
  /**
   * #6139 (PR #6143 HOLD, cure 1) — narration-instruction migration marker.
   * Absent ⇒ the narrator keeps the legacy full instruction block, exactly as
   * for an intent with no authored contract; `'contract_v1'` ⇒ the turn's
   * narration instructions are projected from this contract's typed fields.
   * Closed vocabulary (EXECUTIVE_ANSWER_NARRATION_INSTRUCTION_MODES);
   * lifecycle staging, never routing.
   */
  readonly narrationInstructions?: ExecutiveAnswerNarrationInstructionMode;
  /**
   * Structured supporting-fact admission beyond the obligations. Absent means
   * fail-closed: no supporting facts are admitted beyond what obligations require.
   */
  readonly permittedSupportingRoles?: readonly ExecutiveAnswerPermittedSupportingRole[];
  /**
   * OPTIONAL human-readable description of the scope. Explicitly NON-ENFORCING —
   * admission is governed solely by obligations + permittedSupportingRoles; this note
   * is descriptive metadata for authors and reviewers.
   */
  readonly scopeNote?: string;
};

export type ExecutiveAnswerContractValidation = {
  ok: boolean;
  errors: string[];
};

const CONTRACT_VERSION_PATTERN = /^v?\d+\.\d+\.\d+$/;

/**
 * Structural validation, mirroring `validateGovernedCapabilityManifest` conventions:
 * returns `{ ok, errors }`, error strings are `<path>_required` / `<path>_invalid`
 * style, and the whole contract is walked (no early exit after the first defect).
 */
export function validateExecutiveAnswerContract(
  value: unknown
): ExecutiveAnswerContractValidation {
  const errors: string[] = [];
  const contract = isRecord(value) ? value : null;
  if (!contract) {
    return { ok: false, errors: ['contract_object_required'] };
  }

  const version = contract.contractVersion;
  if (typeof version !== 'string' || version.trim().length === 0) {
    errors.push('contractVersion_required');
  } else if (!CONTRACT_VERSION_PATTERN.test(version.trim())) {
    errors.push('contractVersion_invalid');
  }

  const shape = typeof contract.answerShape === 'string' ? contract.answerShape : null;
  if (!shape) {
    errors.push('answerShape_required');
  } else if (!isOneOf(shape, EXECUTIVE_ANSWER_SHAPES)) {
    errors.push('answerShape_invalid');
  }
  const validShape: ExecutiveAnswerShape | null =
    shape && isOneOf(shape, EXECUTIVE_ANSWER_SHAPES) ? shape : null;

  const basis = contract.comparisonBasis;
  if (basis !== undefined) {
    if (typeof basis !== 'string' || !isOneOf(basis, EXECUTIVE_ANSWER_COMPARISON_BASES)) {
      errors.push('comparisonBasis_invalid');
    } else if (validShape && validShape !== 'comparison') {
      errors.push('comparisonBasis_only_valid_for_comparison_shape');
    }
  } else if (validShape === 'comparison') {
    errors.push('comparisonBasis_required_for_comparison_shape');
  }

  const descriptor = contract.methodDescriptor;
  if (descriptor !== undefined) {
    if (
      typeof descriptor !== 'string' ||
      !isOneOf(descriptor, EXECUTIVE_ANSWER_METHOD_DESCRIPTORS)
    ) {
      errors.push('methodDescriptor_invalid');
    } else if (
      validShape &&
      !EXECUTIVE_ANSWER_METHOD_DESCRIPTOR_SHAPES[descriptor].includes(validShape)
    ) {
      errors.push('methodDescriptor_conflicts_with_answerShape');
    }
  }

  const policy =
    typeof contract.recommendationPolicy === 'string' ? contract.recommendationPolicy : null;
  const validPolicy: ExecutiveAnswerRecommendationPolicy | null =
    policy && isOneOf(policy, EXECUTIVE_ANSWER_RECOMMENDATION_POLICIES) ? policy : null;

  validateObligations(contract.obligations, validShape, validPolicy, errors);

  const closePolicy = contract.executiveClosePolicy;
  if (closePolicy !== undefined) {
    if (closePolicy !== 'required' && closePolicy !== 'optional') {
      errors.push('executiveClosePolicy_invalid');
    } else if (closePolicy === 'required') {
      // #6139 (PR #6143 round-4, cure A) — cross-checks in the pattern of the
      // shape/policy consistency rules below. A compelled executive close IS
      // a compelled recommendation: it cannot coexist with a
      // recommendationPolicy that declares recommendation not applicable,
      // and a pure lookup readout is never forced to recommend — the same
      // logic as `recommendationPolicy_required_conflicts_with_lookup`.
      if (validPolicy === 'not_applicable') {
        errors.push('executiveClosePolicy_required_conflicts_with_not_applicable');
      }
      if (validShape === 'lookup') {
        errors.push('executiveClosePolicy_required_conflicts_with_lookup');
      }
    }
  }

  const narrationInstructionMode = contract.narrationInstructions;
  if (narrationInstructionMode !== undefined) {
    if (
      typeof narrationInstructionMode !== 'string' ||
      !isOneOf(
        narrationInstructionMode,
        EXECUTIVE_ANSWER_NARRATION_INSTRUCTION_MODES
      )
    ) {
      errors.push('narrationInstructions_invalid');
    }
  }

  const methodRef = contract.analyticalMethodRef;
  // Optional fields: an explicitly-undefined key is treated as absent (TS optional
  // semantics and JSON round-trip behaviour), so presence checks test the value.
  if (methodRef !== undefined) {
    if (typeof methodRef !== 'string' || methodRef.trim().length === 0) {
      errors.push('analyticalMethodRef_invalid');
    }
  }
  if (
    validShape &&
    executiveAnswerShapeImpliesComputation(validShape) &&
    (typeof methodRef !== 'string' || methodRef.trim().length === 0)
  ) {
    errors.push('analyticalMethodRef_required_for_computational_shape');
  }

  const evidence = contract.evidenceRequirements;
  if (!Array.isArray(evidence)) {
    errors.push('evidenceRequirements_required');
  } else {
    evidence.forEach((entry, index) => {
      if (typeof entry !== 'string' || entry.trim().length === 0) {
        errors.push(`evidenceRequirements[${index}]_required`);
      }
    });
  }

  if (
    contract.renderContractRef !== undefined &&
    (typeof contract.renderContractRef !== 'string' ||
      contract.renderContractRef.trim().length === 0)
  ) {
    errors.push('renderContractRef_invalid');
  }

  if (!policy) {
    errors.push('recommendationPolicy_required');
  } else if (!validPolicy) {
    errors.push('recommendationPolicy_invalid');
  } else {
    // Shape consistency: a decision exists to recommend — declaring its recommendation
    // not applicable is a contradiction; a pure lookup readout must never be FORCED to
    // recommend ('optional' stays legal, the default for lookup is 'not_applicable').
    if (validShape === 'decision' && validPolicy === 'not_applicable') {
      errors.push('recommendationPolicy_not_applicable_conflicts_with_decision');
    }
    if (validShape === 'lookup' && validPolicy === 'required') {
      errors.push('recommendationPolicy_required_conflicts_with_lookup');
    }
  }

  if (contract.recommendationAuthoritySource !== undefined) {
    const authority = contract.recommendationAuthoritySource;
    if (
      typeof authority !== 'string' ||
      !isOneOf(authority, EXECUTIVE_ANSWER_RECOMMENDATION_AUTHORITY_SOURCES)
    ) {
      errors.push('recommendationAuthoritySource_invalid');
    } else if (validPolicy === 'not_applicable') {
      errors.push('recommendationAuthoritySource_conflicts_with_not_applicable');
    }
  }

  validateSectionPolicy(contract.sectionPolicy, validPolicy, errors);

  if (contract.permittedSupportingRoles !== undefined) {
    validatePermittedSupportingRoles(contract.permittedSupportingRoles, validPolicy, errors);
  }

  if (
    contract.scopeNote !== undefined &&
    (typeof contract.scopeNote !== 'string' || contract.scopeNote.trim().length === 0)
  ) {
    errors.push('scopeNote_invalid');
  }

  // Slice 1's prose relevance fence is RETIRED: prose cannot be machine-enforced.
  // A contract still carrying it is rejected, not silently ignored.
  if (contract.relevanceScope !== undefined) {
    errors.push('relevanceScope_retired_use_permittedSupportingRoles');
  }

  return { ok: errors.length === 0, errors: Array.from(new Set(errors)) };
}

function validateObligations(
  value: unknown,
  shape: ExecutiveAnswerShape | null,
  policy: ExecutiveAnswerRecommendationPolicy | null,
  errors: string[]
): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push('obligations_required');
    return;
  }
  const seen = new Set<string>();
  let hasRequiredObligation = false;
  let everyObligationCarriesPermittedGap = true;
  value.forEach((entry, index) => {
    const obligation = isRecord(entry) ? entry : null;
    if (!obligation) {
      errors.push(`obligations[${index}]_object_required`);
      everyObligationCarriesPermittedGap = false;
      return;
    }
    const obligationId =
      typeof obligation.obligationId === 'string' ? obligation.obligationId.trim() : '';
    if (!obligationId) {
      errors.push(`obligations[${index}].obligationId_required`);
    } else {
      if (seen.has(obligationId)) {
        errors.push(`obligations[${index}].obligationId_duplicate`);
      }
      seen.add(obligationId);
    }
    if (
      typeof obligation.description !== 'string' ||
      obligation.description.trim().length === 0
    ) {
      errors.push(`obligations[${index}].description_required`);
    }
    const role = obligation.role;
    if (typeof role !== 'string' || role.trim().length === 0) {
      errors.push(`obligations[${index}].role_required`);
    } else if (!isOneOf(role, EXECUTIVE_ANSWER_OBLIGATION_ROLES)) {
      errors.push(`obligations[${index}].role_invalid`);
    } else if (role === 'decision' && policy === 'not_applicable') {
      // Decision facts are admissible only under an applicable recommendation policy.
      errors.push(`obligations[${index}].decision_role_conflicts_with_not_applicable`);
    }
    if (typeof obligation.required !== 'boolean') {
      errors.push(`obligations[${index}].required_required`);
    } else if (obligation.required) {
      hasRequiredObligation = true;
    }

    // Kill role-only satisfaction: an obligation must pin HOW it is satisfied — a
    // non-empty governed metricKeys list, or an explicit admissible-binding
    // relationship/grain constraint. Never a bare role.
    let hasMetricKeys = false;
    if (obligation.metricKeys !== undefined) {
      if (!Array.isArray(obligation.metricKeys) || obligation.metricKeys.length === 0) {
        errors.push(`obligations[${index}].metricKeys_invalid`);
      } else {
        hasMetricKeys = true;
        obligation.metricKeys.forEach((key, keyIndex) => {
          if (typeof key !== 'string' || key.trim().length === 0) {
            errors.push(`obligations[${index}].metricKeys[${keyIndex}]_required`);
          }
        });
      }
    }
    if (obligation.bindingPlaceholderRefs !== undefined) {
      if (
        !Array.isArray(obligation.bindingPlaceholderRefs) ||
        obligation.bindingPlaceholderRefs.length === 0
      ) {
        errors.push(`obligations[${index}].bindingPlaceholderRefs_invalid`);
      } else {
        obligation.bindingPlaceholderRefs.forEach((ref, refIndex) => {
          if (typeof ref !== 'string' || ref.trim().length === 0) {
            errors.push(`obligations[${index}].bindingPlaceholderRefs[${refIndex}]_required`);
          }
        });
      }
    }
    let hasAdmissibleBinding = false;
    if (obligation.admissibleBinding !== undefined) {
      hasAdmissibleBinding = true;
      validateAdmissibleBinding(
        obligation.admissibleBinding,
        errors,
        `obligations[${index}].admissibleBinding`
      );
    }
    if (!hasMetricKeys && !hasAdmissibleBinding) {
      errors.push(`obligations[${index}].metricKeys_or_admissibleBinding_required`);
    }

    if (obligation.permittedGap !== undefined) {
      const fallback = isRecord(obligation.permittedGap) ? obligation.permittedGap : null;
      if (!fallback) {
        errors.push(`obligations[${index}].permittedGap_object_required`);
        everyObligationCarriesPermittedGap = false;
      } else {
        if (
          typeof fallback.statement !== 'string' ||
          fallback.statement.trim().length === 0
        ) {
          errors.push(`obligations[${index}].permittedGap.statement_required`);
          everyObligationCarriesPermittedGap = false;
        }
        if (
          fallback.ownerHint !== undefined &&
          (typeof fallback.ownerHint !== 'string' || fallback.ownerHint.trim().length === 0)
        ) {
          errors.push(`obligations[${index}].permittedGap.ownerHint_invalid`);
        }
      }
    } else {
      everyObligationCarriesPermittedGap = false;
    }
  });
  // A contract with no required obligation is hollow — UNLESS it is the explicit
  // lookup-with-gap posture: a lookup readout whose every obligation carries an honest
  // permitted-gap fallback (the module declares it cannot bind yet, and says so).
  if (
    !hasRequiredObligation &&
    !(shape === 'lookup' && everyObligationCarriesPermittedGap)
  ) {
    errors.push('obligations_require_one_required_or_lookup_gap_posture');
  }
}

function validateAdmissibleBinding(value: unknown, errors: string[], errorPath: string): void {
  const binding = isRecord(value) ? value : null;
  if (!binding) {
    errors.push(`${errorPath}_object_required`);
    return;
  }
  const semanticRole = binding.semanticRole;
  if (typeof semanticRole !== 'string' || semanticRole.trim().length === 0) {
    errors.push(`${errorPath}.semanticRole_required`);
  } else if (!isOneOf(semanticRole, SEMANTIC_NUMBER_ROLES)) {
    errors.push(`${errorPath}.semanticRole_invalid`);
  }
  const relationshipType = binding.relationshipType;
  if (typeof relationshipType !== 'string' || relationshipType.trim().length === 0) {
    errors.push(`${errorPath}.relationshipType_required`);
  } else if (!isOneOf(relationshipType, EXECUTIVE_ANSWER_FACT_RELATIONSHIP_TYPES)) {
    errors.push(`${errorPath}.relationshipType_invalid`);
  }
  if (binding.scopeConstraint !== undefined) {
    validateScopeConstraint(binding.scopeConstraint, errors, `${errorPath}.scopeConstraint`);
  }
}

function validateScopeConstraint(value: unknown, errors: string[], errorPath: string): void {
  const constraint = isRecord(value) ? value : null;
  if (!constraint) {
    errors.push(`${errorPath}_object_required`);
    return;
  }
  let constrains = false;
  if (constraint.entityRef !== undefined) {
    if (typeof constraint.entityRef !== 'string' || constraint.entityRef.trim().length === 0) {
      errors.push(`${errorPath}.entityRef_invalid`);
    } else {
      constrains = true;
    }
  }
  if (constraint.grain !== undefined) {
    if (
      typeof constraint.grain !== 'string' ||
      !isOneOf(constraint.grain, EXECUTIVE_ANSWER_SCOPE_GRAINS)
    ) {
      errors.push(`${errorPath}.grain_invalid`);
    } else {
      constrains = true;
    }
  }
  if (!constrains) {
    errors.push(`${errorPath}_constraint_required`);
  }
}

function validatePermittedSupportingRoles(
  value: unknown,
  policy: ExecutiveAnswerRecommendationPolicy | null,
  errors: string[]
): void {
  if (!Array.isArray(value)) {
    errors.push('permittedSupportingRoles_array_required');
    return;
  }
  value.forEach((entry, index) => {
    const admission = isRecord(entry) ? entry : null;
    if (!admission) {
      errors.push(`permittedSupportingRoles[${index}]_object_required`);
      return;
    }
    const role = admission.role;
    if (typeof role !== 'string' || role.trim().length === 0) {
      errors.push(`permittedSupportingRoles[${index}].role_required`);
    } else if (!isOneOf(role, SEMANTIC_NUMBER_ROLES)) {
      errors.push(`permittedSupportingRoles[${index}].role_invalid`);
    }
    const relationshipType = admission.relationshipType;
    if (typeof relationshipType !== 'string' || relationshipType.trim().length === 0) {
      errors.push(`permittedSupportingRoles[${index}].relationshipType_required`);
    } else if (!isOneOf(relationshipType, EXECUTIVE_ANSWER_FACT_RELATIONSHIP_TYPES)) {
      errors.push(`permittedSupportingRoles[${index}].relationshipType_invalid`);
    } else if (relationshipType === 'decision_support' && policy === 'not_applicable') {
      // Decision facts are admissible only under an applicable recommendation policy.
      errors.push(
        `permittedSupportingRoles[${index}].decision_support_conflicts_with_not_applicable`
      );
    }
    if (admission.metricKeyRefs !== undefined) {
      if (!Array.isArray(admission.metricKeyRefs) || admission.metricKeyRefs.length === 0) {
        errors.push(`permittedSupportingRoles[${index}].metricKeyRefs_invalid`);
      } else {
        admission.metricKeyRefs.forEach((key, keyIndex) => {
          if (typeof key !== 'string' || key.trim().length === 0) {
            errors.push(`permittedSupportingRoles[${index}].metricKeyRefs[${keyIndex}]_required`);
          }
        });
      }
    }
    if (admission.bindingPlaceholderRefs !== undefined) {
      if (
        !Array.isArray(admission.bindingPlaceholderRefs) ||
        admission.bindingPlaceholderRefs.length === 0
      ) {
        errors.push(`permittedSupportingRoles[${index}].bindingPlaceholderRefs_invalid`);
      } else {
        admission.bindingPlaceholderRefs.forEach((ref, refIndex) => {
          if (typeof ref !== 'string' || ref.trim().length === 0) {
            errors.push(
              `permittedSupportingRoles[${index}].bindingPlaceholderRefs[${refIndex}]_required`
            );
          }
        });
      }
    }
    if (admission.scopeConstraint !== undefined) {
      validateScopeConstraint(
        admission.scopeConstraint,
        errors,
        `permittedSupportingRoles[${index}].scopeConstraint`
      );
    }
  });
}

/**
 * Section keys whose disposition a contract states in its own canonical field, so
 * `supportingSections` may never restate them — one property, one authority.
 *
 * - `directAnswer` / `headline`: the same obligation under the contract's name and
 *   under the name governed narration renders it with (#4741). `directAnswer` is
 *   typed the literal `'required'` and forced un-overridable above; a
 *   `supportingSections.headline` entry would let the same policy disable the only
 *   section carrying the direct answer it just swore was required.
 * - `recommendation`: already reserved, and forced to agree with
 *   `recommendationPolicy`.
 */
const RESERVED_SECTION_POLICY_KEYS = frozenTuple([
  'directAnswer',
  'headline',
  'recommendation',
] as const);

function isReservedSectionPolicyKey(sectionKey: string): boolean {
  return isOneOf(sectionKey, RESERVED_SECTION_POLICY_KEYS);
}

function validateSectionPolicy(
  value: unknown,
  policy: ExecutiveAnswerRecommendationPolicy | null,
  errors: string[]
): void {
  if (value === undefined) {
    errors.push('sectionPolicy_required');
    return;
  }
  const sectionPolicy = isRecord(value) ? value : null;
  if (!sectionPolicy) {
    errors.push('sectionPolicy_object_required');
    return;
  }
  // The direct answer is the one section no contract may omit or disable.
  if (sectionPolicy.directAnswer !== 'required') {
    errors.push('sectionPolicy.directAnswer_must_be_required');
  }
  const recommendation = sectionPolicy.recommendation;
  if (typeof recommendation !== 'string' || recommendation.trim().length === 0) {
    errors.push('sectionPolicy.recommendation_required');
  } else if (!isOneOf(recommendation, EXECUTIVE_ANSWER_SECTION_DISPOSITIONS)) {
    errors.push('sectionPolicy.recommendation_invalid');
  } else if (policy && recommendation !== SECTION_DISPOSITION_BY_RECOMMENDATION_POLICY[policy]) {
    errors.push('sectionPolicy.recommendation_conflicts_with_recommendationPolicy');
  }
  if (sectionPolicy.supportingSections !== undefined) {
    const supporting = isRecord(sectionPolicy.supportingSections)
      ? sectionPolicy.supportingSections
      : null;
    if (!supporting) {
      errors.push('sectionPolicy.supportingSections_object_required');
      return;
    }
    Object.entries(supporting).forEach(([sectionKey, disposition]) => {
      if (sectionKey.trim().length === 0) {
        errors.push('sectionPolicy.supportingSections_key_required');
        return;
      }
      if (isReservedSectionPolicyKey(sectionKey)) {
        errors.push(`sectionPolicy.supportingSections.${sectionKey}_reserved`);
        return;
      }
      if (
        typeof disposition !== 'string' ||
        !isOneOf(disposition, EXECUTIVE_ANSWER_SECTION_DISPOSITIONS)
      ) {
        errors.push(`sectionPolicy.supportingSections.${sectionKey}_invalid`);
      }
    });
  }
}

function deepFrozenSubstitutions(
  pairs: ProhibitedRoleSubstitution[]
): readonly ProhibitedRoleSubstitution[] {
  return Object.freeze(pairs.map((pair) => Object.freeze({ ...pair })));
}

function isOneOf<T extends readonly string[]>(value: string, values: T): value is T[number] {
  return (values as readonly string[]).includes(value);
}

function frozenTuple<const T extends readonly string[]>(values: T): Readonly<T> {
  return Object.freeze([...values]) as unknown as Readonly<T>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
